import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TDocumentDefinitions, Content, TableCell } from 'pdfmake/interfaces';
import { YearlyActivity, COST_SOURCE_LABELS } from './maintenanceCostUtils';
import { analyticsApi } from '../api/analyticsApi';

// Initialize pdfmake fonts
(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs || pdfFonts;

/**
 * Sanitize filename for cross-platform compatibility
 */
function sanitizeFilename(name: string): string {
    return name
        .replace(/[<>:"/\\|?*]/g, '') // Remove invalid chars
        .replace(/\s+/g, '-') // Replace spaces with dashes
        .substring(0, 100); // Limit length
}

/**
 * Escape CSV field - handles semicolons, quotes, and newlines
 */
function escapeCSVField(value: string | number | undefined): string {
    if (value === undefined || value === null) return '';
    const str = String(value);
    if (str.includes(';') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

/**
 * Get filter description in Swedish
 */
function getFilterDescription(yearsToShow: number, costTypeFilter: 'all' | 'maintenance' | 'replacement'): string {
    const costTypeText = {
        all: 'Alla kostnader',
        maintenance: 'Underhåll',
        replacement: 'Investering'
    }[costTypeFilter];
    return `${yearsToShow} år, ${costTypeText}`;
}

/**
 * Format date for display in Swedish
 */
function formatSwedishDate(date: Date): string {
    const months = [
        'januari', 'februari', 'mars', 'april', 'maj', 'juni',
        'juli', 'augusti', 'september', 'oktober', 'november', 'december'
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Calculate cost based on filter
 */
function getFilteredCost(
    cost: number,
    investmentPercentage: number | undefined,
    costTypeFilter: 'all' | 'maintenance' | 'replacement'
): number {
    const pct = investmentPercentage ?? 0;
    if (costTypeFilter === 'all') return cost;
    if (costTypeFilter === 'replacement') return cost * pct / 100;
    return cost * (100 - pct) / 100;
}

/**
 * Calculate investment amount from cost and percentage
 */
function getInvestmentAmount(cost: number, investmentPercentage: number | undefined): number {
    const pct = investmentPercentage ?? 0;
    return Math.round(cost * pct / 100);
}

/**
 * Export maintenance plan to CSV format
 */
export function exportMaintenancePlanToCSV(
    yearlyPlans: YearlyActivity[],
    buildingName: string,
    yearsToShow: number,
    costTypeFilter: 'all' | 'maintenance' | 'replacement',
    getTranslation?: (key: string | undefined) => string
): void {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];

    // Filter to years within range
    const currentYear = today.getFullYear();
    const endYear = currentYear + yearsToShow;
    const filteredPlans = yearlyPlans.filter(p => p.year >= currentYear && p.year <= endYear);

    // Build CSV content with UTF-8 BOM for Excel
    const BOM = '\uFEFF';
    const headers = ['År', 'Komponent', 'Komponenttyp', 'Aktivitetstyp', 'Aktivitet', 'Kostnad (kr)', 'Kostnadsunderlag', 'Varav investering (kr)'];

    const rows: string[] = [headers.join(';')];

    filteredPlans.forEach(yearData => {
        yearData.activities.forEach(activity => {
            const filteredCost = getFilteredCost(activity.cost, activity.investmentPercentage, costTypeFilter);
            if (filteredCost === 0 && costTypeFilter !== 'all') return; // Skip zero-cost items when filtered

            const activityTypeText = activity.activityType === 'replacement' ? 'Byte' : 'Underhåll';
            const translatedType = getTranslation ? getTranslation(activity.componentType) : activity.componentType;
            const displayType = translatedType || 'Okänd typ';
            const investmentAmount = getInvestmentAmount(filteredCost, activity.investmentPercentage);

            const row = [
                escapeCSVField(yearData.year),
                escapeCSVField(activity.componentName),
                escapeCSVField(displayType),
                escapeCSVField(activityTypeText),
                escapeCSVField(activity.activityName),
                escapeCSVField(Math.round(filteredCost)),
                escapeCSVField(COST_SOURCE_LABELS[activity.costSource ?? 'template']),
                escapeCSVField(investmentAmount)
            ];
            rows.push(row.join(';'));
        });
    });

    const csvContent = BOM + rows.join('\n');
    const filename = `Underhallsplan-${sanitizeFilename(buildingName)}-${dateStr}.csv`;

    // Download using Blob + anchor pattern
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    analyticsApi.trackReportGenerated('maintenance_plan_csv');
}

/**
 * Export maintenance plan to PDF format
 */
export async function exportMaintenancePlanToPDF(
    yearlyPlans: YearlyActivity[],
    buildingName: string,
    yearsToShow: number,
    costTypeFilter: 'all' | 'maintenance' | 'replacement',
    getTranslation?: (key: string | undefined) => string
): Promise<void> {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];

    // Filter to years within range
    const currentYear = today.getFullYear();
    const endYear = currentYear + yearsToShow;
    const filteredPlans = yearlyPlans.filter(p => p.year >= currentYear && p.year <= endYear);

    // Calculate totals
    let totalCost = 0;
    let maintenanceCost = 0;
    let investmentCost = 0;

    filteredPlans.forEach(yearData => {
        yearData.activities.forEach(activity => {
            const filteredCost = getFilteredCost(activity.cost, activity.investmentPercentage, costTypeFilter);
            totalCost += filteredCost;

            const pct = activity.investmentPercentage ?? 0;
            const maintenance = activity.cost * (100 - pct) / 100;
            const investment = activity.cost * pct / 100;

            if (costTypeFilter === 'all') {
                maintenanceCost += maintenance;
                investmentCost += investment;
            } else if (costTypeFilter === 'maintenance') {
                maintenanceCost += maintenance;
            } else {
                investmentCost += investment;
            }
        });
    });

    // Build table body
    const tableBody: TableCell[][] = [
        // Header row
        [
            { text: 'År', style: 'tableHeader' },
            { text: 'Komponent', style: 'tableHeader' },
            { text: 'Typ', style: 'tableHeader' },
            { text: 'Aktivitet', style: 'tableHeader' },
            { text: 'Kostnad (kr)', style: 'tableHeader', alignment: 'right' },
            { text: 'Varav inv.', style: 'tableHeader', alignment: 'right' }
        ]
    ];

    filteredPlans.forEach(yearData => {
        yearData.activities.forEach(activity => {
            const filteredCost = getFilteredCost(activity.cost, activity.investmentPercentage, costTypeFilter);
            if (filteredCost === 0 && costTypeFilter !== 'all') return;

            const activityTypeText = activity.activityType === 'replacement' ? 'Byte' : 'Underhåll';
            const translatedType = getTranslation ? getTranslation(activity.componentType) : activity.componentType;
            const displayType = translatedType || 'Okänd typ';
            const investmentAmount = getInvestmentAmount(filteredCost, activity.investmentPercentage);

            tableBody.push([
                { text: yearData.year.toString() },
                { text: activity.componentName },
                { text: displayType },
                { text: `${activityTypeText}: ${activity.activityName}` },
                { text: Math.round(filteredCost).toLocaleString('sv-SE'), alignment: 'right' },
                { text: investmentAmount.toLocaleString('sv-SE'), alignment: 'right' }
            ]);
        });
    });

    // Build document definition
    const content: Content = [
        // Header
        { text: `Underhållsplan: ${buildingName}`, style: 'header' },
        {
            text: `Exporterad: ${formatSwedishDate(today)}`,
            style: 'subheader'
        },
        {
            text: `Visar: ${getFilterDescription(yearsToShow, costTypeFilter)}`,
            style: 'subheader',
            margin: [0, 0, 0, 20]
        },

        // Summary section
        { text: 'SAMMANFATTNING', style: 'sectionHeader' },
        {
            columns: [
                { text: `Total kostnad: ${Math.round(totalCost).toLocaleString('sv-SE')} kr`, width: 'auto' }
            ],
            margin: [0, 5, 0, 5]
        }
    ];

    // Add breakdown if showing all costs
    if (costTypeFilter === 'all') {
        content.push({
            columns: [
                { text: `Underhåll: ${Math.round(maintenanceCost).toLocaleString('sv-SE')} kr`, width: 'auto' },
                { text: `Investering: ${Math.round(investmentCost).toLocaleString('sv-SE')} kr`, width: 'auto', margin: [20, 0, 0, 0] }
            ],
            margin: [0, 0, 0, 20]
        } as Content);
    } else {
        content.push({ text: '', margin: [0, 0, 0, 15] } as Content);
    }

    // Activities table
    content.push(
        { text: 'AKTIVITETER', style: 'sectionHeader' },
        {
            table: {
                headerRows: 1,
                widths: ['auto', '*', 'auto', '*', 'auto', 'auto'],
                body: tableBody
            },
            layout: {
                fillColor: (rowIndex: number) => rowIndex === 0 ? '#f0f0f0' : null,
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#e0e0e0',
                vLineColor: () => '#e0e0e0'
            }
        } as Content
    );

    const docDefinition: TDocumentDefinitions = {
        content,
        styles: {
            header: {
                fontSize: 18,
                bold: true,
                margin: [0, 0, 0, 5]
            },
            subheader: {
                fontSize: 10,
                color: '#666666',
                margin: [0, 0, 0, 3]
            },
            sectionHeader: {
                fontSize: 12,
                bold: true,
                margin: [0, 10, 0, 5]
            },
            tableHeader: {
                bold: true,
                fontSize: 10,
                fillColor: '#f0f0f0'
            }
        },
        defaultStyle: {
            fontSize: 9
        },
        pageSize: 'A4',
        pageMargins: [40, 40, 40, 40]
    };

    const filename = `Underhallsplan-${sanitizeFilename(buildingName)}-${dateStr}.pdf`;

    // Generate and download PDF
    pdfMake.createPdf(docDefinition).download(filename);

    analyticsApi.trackReportGenerated('maintenance_plan_pdf');
}
